<template>
	<ListOfferMBRExp
		v-if="expMergeBuyRentVariant && expMergeBuyRentVariant !== 'control'"
		:offer="offer"
		:title="title"
		:isPromotedBuyBox="isPromotedBuyBox"
		:isBundle="isBundle"
		:isEpisode="isEpisode"
		:seasonNumber="seasonNumber"
		:episodeNumber="episodeNumber"
		:additionalContexts="additionalContexts"
	/>
	<div v-else ref="offerContainer" class="offer-container">
		<div v-if="isLeavingIconShown" class="leaving-banner-container">
			<div class="leaving-banner">
				<FontAwesomeIcon :icon="faClock" />
				<span> {{ leavingLabel }}</span>
			</div>
		</div>

		<component
			:is="isModalProvider ? 'div' : ClickoutAnchor"
			:rel="isSponsoredOffer"
			:referrerpolicy="shouldMaskClicks ? 'no-referrer' : undefined"
			v-bind="isModalProvider ? {} : { href: offerUrl }"
			clickoutType="regular"
			class="offer"
			:class="{ 'plex-offer': isModalProvider }"
			@click="isModalProvider ? openModal() : handleClickOut()"
			v-observe-visibility="observeVisibilityOptions"
		>
			<div class="offer__content">
				<div class="content-wrapper content-wrapper--left">
					<div class="picture-wrapper">
						<ProviderIconWide
							v-if="!isBundle || isBundleWithIconWide"
							:provider="hardcodedPackage"
							loading="eager"
						/>
						<BundleIcons
							v-else-if="(buyboxOffer.icons || [])?.length > 0"
							:packageIcons="buyboxOffer.icons"
						/>
						<span v-if="isProviderPromo && promoRibbonText" class="ribbon">
							{{ promoRibbonText }}
						</span>
					</div>

					<ListOfferPresentation
						:offer="buyboxOffer"
						:title="title"
						:seasonNumber="seasonNumber"
						@openBuyBoxInfoModal="toggleBuyBoxInfoModal(true)"
					/>
				</div>

				<div class="content-wrapper content-wrapper--right">
					<div class="offer__label">
						<span v-if="isProviderPromo && providerPromo?.offerText" class="offer__label__text">
							{{ offerLabel(buyboxOffer?.monetizationType, buyboxOffer.presentationType) }}
							<DisclaimerButton v-if="showDisclaimerButton" :disclaimerText="disclaimerText" />
						</span>
						<span v-else-if="hasFreeTrial" class="offer__label__free-trial">
							{{ freeTrialText }}
							<DisclaimerButton v-if="showDisclaimerButton" :disclaimerText="disclaimerText" />
						</span>
						<p v-else class="offer__label__text">
							{{ offerLabel(buyboxOffer?.monetizationType, buyboxOffer.presentationType) }}
							<DisclaimerButton
								v-if="showDisclaimerButton && !isBundle"
								:disclaimerText="disclaimerText"
							/>
						</p>

						<template v-if="isShowPageTVOD && hasMinPriceTVOD">
							<p v-if="!!minEpisodeTVODPriceOnShows" class="offer__label__price">
								{{
									$t('WEBAPP_PRICE_PER_EPISODE', {
										price: minEpisodeTVODPriceOnShows,
									})
								}}
							</p>
							<p
								v-else-if="minSeasonTVODPriceOnShows"
								class="offer__label__price offer__label__price--breakdown"
							>
								{{ minSeasonTVODPriceOnShowsText }}
							</p>
						</template>

						<template v-else>
							<p v-if="hasOfferDetails" class="offer__label__price">
								{{ offerDetails }}
							</p>
							<p v-else-if="showRetailPrice" class="offer__label__price">
								<span>{{ retailPrice }}</span>
							</p>
							<!-- This will still make sure the space is still taken up (it's transparent) -->
							<p
								v-else-if="buyboxOffer.monetizationType !== 'FAST' || !formattedDate"
								class="offer__label__price transparent"
							>
								retail price
							</p>
							<p
								v-if="buyboxOffer.monetizationType === 'FAST' && formattedDate"
								class="offer__label__price price-fast"
							>
								{{ formattedDate['PST'] }} / <br />
								{{ formattedDate['EST'] }}
							</p>
						</template>
					</div>
					<ListOfferCTA :offer="buyboxOffer" :isBundle="!!isBundle" />
				</div>
			</div>
			<div class="offer__detail" v-show="false"></div>
		</component>

		<BuyBoxInfoModal
			:displayBuyBoxInfoModal="displayBuyBoxInfoModal"
			:title="title"
			:offer="offer"
			:seasonNumber="seasonNumber"
			:episodeNumber="episodeNumber"
			@close="toggleBuyBoxInfoModal($event)"
		/>

		<!-- Native Ads -->
		<NativeAdListBuyboxOffer
			v-if="isNativeAdCandidate"
			:offer="buyboxOffer"
			:title="title"
			:isPromotedBuyBox="isPromotedBuyBox"
			:episodeNumber="episodeNumber"
			:rowIndex="nativeAdRowIndex"
		/>
	</div>
</template>
<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { MonetizationType, ObjectType, OfferType, PresentationType } from '@/@types/graphql-types';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import {
	BuyboxParam,
	DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY,
	MONETIZATION_TYPE_TRANSLATION_KEY,
	OfferLocation,
} from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { shouldClickMask, clickMaskUrl } from '@/components/click-masking/click-masking-config';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';

import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { diffInDays, formatDateTime } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIconWide from '@/features/ui/components/ProviderIconWide.vue';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { getClickoutTrackingPlacement, OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import { useProviderPromotion } from '@/helpers/provider-promotion';

import {
	useInternationalOffers,
	useInternationalOffersStatus,
} from '@/features/international-offers/composables/useInternationalOffers';

import { faClock } from '@fortawesome/free-solid-svg-icons';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';
import { getDisclaimerText } from '@/features/buybox/helpers/list-buybox-config';

import { useExperiment } from '@/libraries/experiment';

import { BuyboxRegularOffer } from '@/features/buybox/types/types';

const BuyBoxInfoModal = defineAsyncComponent(() => import('@/features/buybox/components/list/BuyBoxInfoModal.vue'));
const BundleIcons = defineAsyncComponent(() => import('@/features/buybox/components/list/BundleIcons.vue'));
import ListOfferCTA from '@/features/buybox/components/list/ListOfferCTA.vue';
import ListOfferPresentation from '@/features/buybox/components/list/ListOfferPresentation.vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useDoubleStackExp } from '@/components/experiments/DoubleStackExp';

import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { useBuyRentExp } from '@/components/experiments/ExpBuyRent';
import { useSofterFreeTrialV2Exp } from '@/components/experiments/SoftFreeTrialListV2';
import { usePrimePlayPromoExp } from '@/components/experiments/PrimePlayPromoExp';
const ListOfferMBRExp = () => import('@/components/experiments/ExpBuyRent/components/ListOfferMBRExp.vue');
import NativeAdListBuyboxOffer from '@/features/native-ads/components/NativeAdListBuyboxOffer.vue';
import { useNativeAds } from '@/features/native-ads/composables/useNativeAds';

const props = defineProps<{
	offer: BuyboxRegularOffer;
	title: TitleDetail;
	isPromotedBuyBox?: boolean;
	isBundle?: boolean;
	isEpisode?: boolean;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	additionalContexts: SnowplowContext[];
	rowIndex?: number;
}>();

defineEmits<{
	(
		e: 'clickOut',
		data: {
			buyboxOffer: BuyboxRegularOffer;
			isBundle: boolean;
			isLeavingIconShown: boolean;
			row: HTMLElement | null;
		}
	): void;
}>();
const { country, language, webLocale } = storeToRefs(useLanguageStore());

const { t } = useI18n();
const { xServeTrack } = storeToRefs(useAppStore());

const buyboxOffer = computed(() => props.offer);

/* Native Ads
 * Renders a GAM replica of the Ad absolutely positioned on top of existing ad
 */
const { nativeAdsShortName } = useNativeAds({ title: () => props.title });
const isNativeAdCandidate = computed(
	() => !!nativeAdsShortName.value && props.offer.package.shortName === nativeAdsShortName.value && !props.isBundle
);

const { setDoubleStackTags } = useDoubleStackExp();
const { setBuyRentExpTags, trackingEventLabel } = useBuyRentExp({ title: () => props.title, offer: () => props.offer });
const { setSofterFreeTrialV2Tags } = useSofterFreeTrialV2Exp();

const isDvdOrBluRay = computed(() =>
	[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
		buyboxOffer.value?.presentationType
	)
);

const { activeVariantWithControlGroup: expMergeBuyRentVariant, trackEvent: trackBuyRentExpEvent } =
	useExperiment('EXP_MERGE_BUY_RENT');

const isBundleWithIconWide = computed(() => props.isBundle && buyboxOffer.value.bundleShortName === 'pap');

const hardcodedPackage = computed(() => {
	if (isBundleWithIconWide.value) {
		return {
			...buyboxOffer.value.package,
			iconWide: 'https://www.justwatch.com/appassets/img/bundles/applepeacock.{format}',
		};
	}

	return buyboxOffer.value.package;
});

const displayBuyBoxInfoModal = ref<boolean>(false);
const offerContainer = ref<HTMLElement | null>(null);

const isModalProvider = computed(() =>
	['plexplayer', 'justwatchtv', 'ottstudioplayer'].includes(buyboxOffer.value.package.technicalName)
);

const _clickoutType = computed(() => {
	if (isModalProvider.value) return 'video_player';
	return props.isPromotedBuyBox ? 'promoted' : 'regular';
});

const { clickoutType, placement } = getClickoutTrackingPlacement(_clickoutType.value);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const offerLocationInjection = inject<OfferLocation>('offerLocation');
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const noOffer = inject<boolean | undefined>('noOffer', undefined);

const { isPromotionOffer, providerPromotion: providerPromo } = useProviderPromotion();

const isProviderPromo = computed(() => !buyboxOffer.value.bundleId && isPromotionOffer(buyboxOffer.value));

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage({ entityId: jwRawEntityId });

const formattedDate = computed(() => {
	const ESTTimeZone = 'America/New_York';
	const PSTTimeZone = 'America/Los_Angeles';

	if (!buyboxOffer.value?.availableFromTime) {
		return null;
	}

	return {
		Day: formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
			day: 'numeric',
			month: 'short',
			timeZone: ESTTimeZone,
		}),
		PST:
			formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: PSTTimeZone,
			}) + ' PST',
		EST:
			formatDateTime(new Date(buyboxOffer.value.availableFromTime), language.value, country.value, {
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: ESTTimeZone,
			}) + ' EST',
	};
});

const shouldMaskClicks = computed(() => shouldClickMask(buyboxOffer.value?.package.packageId, country.value));

const rowNumber = computed(() => {
	if (offerContainer.value == null) return undefined;

	const parentBuyBox = offerContainer.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(offerContainer.value));

	return index === -1 ? undefined : index;
});

const nativeAdRowIndex = computed(() => props.rowIndex ?? rowNumber.value);

const promoRibbonText = computed(() => providerPromo.value?.wideRibbon ?? providerPromo.value?.ribbon);

const disclaimerText = computed(() => {
	if (isProviderPromo.value && providerPromo.value?.offerText && providerPromo.value.offerDisclaimerText)
		return providerPromo.value.offerDisclaimerText;

	if (props.offer.package.shortName === 'adb' && isDvdOrBluRay.value) {
		return t('WEBAPP_AMAZON_DVD_BLURAY_PRICE_DISCLAIMER');
	}

	return getDisclaimerText(props.offer.package.shortName, country.value);
});

const showDisclaimerButton = computed(() => !!disclaimerText.value);

const { rowCountry } = useInternationalOffers();
const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ title: () => props.title });

const { setPrimePlayPromoTags } = usePrimePlayPromoExp();

function clickoutBase(url: ClickoutUrl) {
	return url
		.addContexts(titleContext.value)
		.setForTracking('offerLocation', offerLocationInjection)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.updateContext(
			'ClickoutContextGraphql',
			'placement',
			isInternationalOffersActive.value ? `international_offers${props.isEpisode ? '_episode' : ''}` : placement
		)
		.updateContext('ClickoutContextGraphql', 'clickoutType', clickoutType)
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.set('uct_buybox', props.isPromotedBuyBox ? BuyboxParam.Promoted : BuyboxParam.Normal)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.setCanary(xServeTrack.value)
		.setGenre(props.title.content.genres)
		.setPageType(props.title.objectType);
}

const offerUrl = computed(() => {
	let clickoutUrl = new ClickoutUrl(buyboxOffer.value);

	if (isProviderPromo.value && providerPromo.value?.url) {
		const offer = { ...buyboxOffer.value, standardWebURL: providerPromo.value.url };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (shouldMaskClicks.value) {
		const maskedUrl = clickMaskUrl(buyboxOffer.value.standardWebURL!);
		const offer = { ...buyboxOffer.value, standardWebURL: maskedUrl };
		clickoutUrl = new ClickoutUrl(offer).skipRedirect();
	}

	if (isArbitrating.value && ['amp', 'atp'].includes(buyboxOffer.value.package.shortName)) {
		const arbitrageUrl = withArbitrageTracking({ offer: buyboxOffer, title: () => props.title, placement: 'ro' });

		if (arbitrageUrl) {
			return arbitrageUrl.apply(clickoutBase);
		}
	}

	const url = clickoutUrl.apply(clickoutBase);

	if (rowCountry.value) {
		url.set('uct_country', rowCountry.value);
	}

	if (props.isBundle) {
		const packageContext = new SnowplowPackageContext(
			buyboxOffer.value.package.packageId,
			buyboxOffer.value.package.technicalName,
			'bundle',
			buyboxOffer.value.bundleId,
			buyboxOffer.value.bundleTechnicalName
		);
		clickoutUrl.addContexts(packageContext);

		// Bypass UCT for US users on bundle amazonstarzamazonhbomax
		if (buyboxOffer.value.bundleTechnicalName === 'amazonstarzamazonhbomax') {
			url.set('uct_ct', 'bypass');
		}
	}

	url.apply(url => setDoubleStackTags(url, 'ro', buyboxOffer.value.package.shortName));

	// EXP MERGE BUY RENT
	url.apply(url =>
		setBuyRentExpTags({
			url: url,
			type: 'ro',
			shortName: buyboxOffer.value.package.shortName,
			monetizationType: buyboxOffer.value.monetizationType,
		})
	);
	// EXP MERGE BUY RENT
	url.apply(u =>
		setSofterFreeTrialV2Tags(
			u,
			buyboxOffer.value.package.shortName,
			props.isPromotedBuyBox ? 'po' : 'ro',
			props.title.objectType,
			buyboxOffer.value.package.technicalName
		)
	);

	url.apply(url =>
		setPrimePlayPromoTags(url, 'ro', buyboxOffer.value.package.shortName, buyboxOffer.value.package.technicalName)
	);

	// Amazon streaming bounty redirects
	if ([9, 119].includes(buyboxOffer.value.package.packageId)) {
		const redirect = url.get('r');
		return url.set('r', redirect!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const isLeavingIconShown = computed(() => {
	if (isNativeAdCandidate.value) return false;
	return (
		!!buyboxOffer.value.availableTo &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) < 30 &&
		diffInDays(new Date(buyboxOffer.value.availableTo)) >= 0
	);
});

const leavingLabel = computed(() => {
	if (!isLeavingIconShown.value) return '';
	const diffDays = diffInDays(new Date(buyboxOffer.value.availableTo));
	if (diffDays === 0) return t('WEBAPP_LEAVING_TODAY');
	if (diffDays === 1) return t('WEBAPP_LEAVING_TOMORROW');
	return t('WEBAPP_LEAVING_X_DAYS', { days: diffDays });
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

const selectedPackagePlan = computed(() => {
	// we have plan offers in 2 separate places because of international offers.
	// for gql it's not easily doable to include international offer's provider plan offers in the package.
	// that's why we take both places here into account.
	const planOffers = buyboxOffer.value?.plans.length
		? buyboxOffer.value?.plans
		: buyboxOffer.value?.package.planOffers;
	if (planOffers.length) {
		const plan = planOffers[0];
		// if a plan has a children that means it's a free trial plan and that always has priority.
		if (plan.children.length > 0) {
			return {
				...plan.children[0],
				retailPrice: plan.retailPrice,
				retailPriceConverted: plan.retailPriceConverted,
			};
		}
		return plan;
	}
	return null;
});

const freeTrialText = computed(() => {
	if (props.isBundle) {
		return null;
	}
	// Free trial data from API
	if (selectedPackagePlan.value?.isTrial) {
		if (selectedPackagePlan.value.durationDays === 1) return t('WEBAPP_1_DAY_FREE');
		return t('WEBAPP_X_DAYS_FREE', {
			days: selectedPackagePlan.value.durationDays,
		});
	}
	return null;
});

const hasFreeTrial = computed(() => {
	return !!freeTrialText.value && buyboxOffer.value?.monetizationType === MonetizationType.Flatrate;
});

// currently used by Sky DE only, this replaces the retail price with 'Sky Angebote' text
const hasOfferDetails = computed(() => buyboxOffer.value.package.shortName === 'skg' && country.value === 'DE');
const offerDetails = computed(() => {
	const provider = buyboxOffer.value.package.shortName === 'skg' ? 'Sky' : buyboxOffer.value.package.technicalName;

	return t('WEBAPP_PROVIDER_OFFER', { provider });
});

const retailPrice = computed(() => {
	if (props.isBundle) {
		return null;
	} else if (buyboxOffer.value?.retailPrice) {
		if (
			buyboxOffer.value.country !== country.value &&
			buyboxOffer.value.retailPriceConverted &&
			buyboxOffer.value.retailPrice != buyboxOffer.value.retailPriceConverted
		) {
			return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
		}
		return buyboxOffer.value.retailPrice;
	} else if (selectedPackagePlan.value && selectedPackagePlan.value.retailPrice) {
		// Retail price from API

		let retailPrice = selectedPackagePlan.value.retailPrice;
		if (
			buyboxOffer.value.country !== country.value &&
			selectedPackagePlan.value?.retailPriceConverted &&
			selectedPackagePlan.value.retailPrice != selectedPackagePlan.value.retailPriceConverted
		) {
			retailPrice = `${selectedPackagePlan.value.retailPrice} (${selectedPackagePlan.value.retailPriceConverted})`;
		}

		return t(
			buyboxOffer.value.monetizationType === MonetizationType.Flatrate && hasFreeTrial.value
				? 'WEBAPP_THEN_X_PER_MONTH'
				: 'WEBAPP_X_PER_MONTH',
			{ price: retailPrice }
		);
	}
	return null;
});

const isShowPageTVOD = computed(
	() =>
		[MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType) &&
		props.title.objectType === ObjectType.Show &&
		!props.isEpisode
);

const minEpisodeTVODPriceOnShows = computed(() => {
	if (
		!isShowPageTVOD.value ||
		props.title.__typename !== 'Show' ||
		!buyboxOffer.value.minRetailPrice ||
		isDvdOrBluRay.value
	)
		return null;

	return buyboxOffer.value.minRetailPrice;
});

const minSeasonTVODPriceOnShows = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}
	// all seasonIds that are on the Show offer
	// we heuristically calculate the min price from the firstSeason, so we don't need to query all seasons and all its offers
	const filteredSeasons = props.title.firstSeason.filter(season => seasonIdsOnOffer.includes(season.id));
	if (!filteredSeasons.length) return null;

	// find the lowest price of all seasons
	const formattedMinPrice = filteredSeasons
		.map(season =>
			season.offers.find(
				offer =>
					offer.monetizationType === buyboxOffer.value.monetizationType &&
					buyboxOffer.value.package.shortName === offer.package.shortName &&
					buyboxOffer.value.presentationType === offer.presentationType
			)
		)
		.filter(offer => offer?.retailPrice != null && offer?.retailPriceValue != null && offer?.retailPriceValue !== 0)
		.sort((a, b) => a!.retailPriceValue! - b!.retailPriceValue!)[0]?.retailPrice;

	return formattedMinPrice;
});

const minSeasonTVODPriceOnShowsText = computed(() => {
	const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
	if (
		props.title.__typename !== 'Show' ||
		!seasonIdsOnOffer.length ||
		![MonetizationType.Buy, MonetizationType.Rent].includes(buyboxOffer.value.monetizationType)
	) {
		return null;
	}

	if (isDvdOrBluRay.value) {
		const seasonIdsOnOffer = buyboxOffer.value.offerSeasons ?? [];
		// we heuristically calculate the min price from the firstSeason, so we don't need to query all seasons and all its offers
		const filteredSeasons = props.title.firstSeason.filter(
			season =>
				seasonIdsOnOffer.includes(season.id) &&
				season.offers.find(
					offer =>
						offer.monetizationType === buyboxOffer.value.monetizationType &&
						buyboxOffer.value.package.shortName === offer.package.shortName &&
						buyboxOffer.value.presentationType === offer.presentationType
				)
		);
		if (buyboxOffer.value.type === OfferType.Standard) {
			if (buyboxOffer.value.country !== country.value && buyboxOffer.value.retailPriceConverted) {
				return `${buyboxOffer.value.retailPrice} (${buyboxOffer.value.retailPriceConverted})`;
			}
			return buyboxOffer.value.retailPrice;
		}
		if (filteredSeasons.length <= 1) {
			return minSeasonTVODPriceOnShows.value;
		}
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	} else {
		return t('WEBAPP_MIN_PRICE_PER_SEASON', {
			price: minSeasonTVODPriceOnShows.value,
		});
	}
});

const hasMinPriceTVOD = computed(() => !!minEpisodeTVODPriceOnShows.value || !!minSeasonTVODPriceOnShows.value);

const showRetailPrice = computed(
	() => !!retailPrice.value && buyboxOffer.value?.monetizationType !== MonetizationType.Free
);

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(buyboxOffer.value?.package.packageId, webLocale.value)
		? 'nofollow sponsored noopener'
		: 'nofollow noopener'
);

const playerModalByProvider: Record<string, { cmp: () => Promise<any> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	justwatchtv: {
		cmp: () => import('@/ui-components/video-player-modals/JwTVModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
};

const { openRentalModal } = useRentalModal();
async function openModal() {
	if (!isModalProvider.value) return;

	if (isJWTVRentalOffer(buyboxOffer.value)) {
		// we trigger clickout first to get the final link context id as it's used to link events especially after page redirects.
		trackClickout(offerUrl.value);
		openRentalModal({
			offer: buyboxOffer.value,
			title: props.title,
			clickoutUrl: offerUrl.value,
			forceSafeOpen: isInternationalOffersActive.value,
		});
		return;
	}

	trackClickout(offerUrl.value);

	const modalData = playerModalByProvider[buyboxOffer.value.package.technicalName];
	ModalHelper.openModal(
		modalData.cmp,
		{
			offer: buyboxOffer.value,
			title: props.title,
			additionalContexts: getClickoutUrlContextsArray(offerUrl.value),
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	handleClickOut();
}

function toggleBuyBoxInfoModal(display: boolean) {
	displayBuyBoxInfoModal.value = display;

	if (displayBuyBoxInfoModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_info_clicked', label: 'buybox' },
			props.additionalContexts
		);
	}
}

function handleClickOut() {
	trackTagEvent(() => buyboxOffer.value);

	// EXP MERGE BUY RENT
	if (trackingEventLabel.value) {
		trackBuyRentExpEvent({
			action: 'click',
			label: trackingEventLabel.value,
			contexts: [offerUrl.value.contexts.ClickoutContextGraphql],
		});
	}
	// EXP MERGE BUY RENT
}

function onVisibilityChanged(isVisible: boolean) {
	onIntersection(offerUrl.value, isVisible);
}

function offerLabel(monetizationType: MonetizationType, presentationType: PresentationType) {
	if (isProviderPromo.value && providerPromo.value?.offerText) return providerPromo.value.offerText;
	if (props.isBundle) {
		return t('WEBAPP_BUNDLE');
	}

	if (monetizationType === MonetizationType.Fast && formattedDate.value?.['Day']) {
		return formattedDate.value?.['Day'];
	}

	if (
		presentationType === PresentationType.Dvd ||
		presentationType === PresentationType.Bluray ||
		presentationType === PresentationType.Bluray_4K
	) {
		if (monetizationType in DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType]) {
			return t(DVD_BLURAY_MONETIZATION_TYPE_TRANSLATION_KEY[presentationType][monetizationType] as string);
		}
	}

	return t(MONETIZATION_TYPE_TRANSLATION_KEY[monetizationType]);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

.content-wrapper {
	flex: 1;
	display: flex;
	justify-content: space-between;

	@media (max-width: 1024px) {
		flex-direction: column;
		justify-content: flex-start;
		width: 50%;
	}
	&--left {
		align-items: flex-start;

		@media (max-width: 1345px) {
			justify-content: flex-start;
		}
	}

	&--right {
		flex: 1;
		align-items: flex-start;
		max-width: 50%;

		@media (max-width: 1024px) {
			flex: 0 1 auto;
			align-items: flex-end;
			max-width: none;
		}
	}
}

.offer {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 48px;
	width: 100%;
	height: 104px;
	padding: 16px;
	background-color: inherit;

	@media (max-width: 1024px) {
		flex-direction: column;
		height: auto;
		gap: 16px;
	}

	&.plex-offer {
		cursor: pointer;
		border: none;
		margin: 0px;
		text-align: left;
	}

	&__content {
		display: flex;
		gap: 16px;
		align-items: flex-start;
		justify-content: space-between;
		width: 100%;

		@media (max-width: 1024px) {
			gap: 8px;
		}
	}

	&__label {
		@media (max-width: 1024px) {
			margin-bottom: 10px;
			text-align: end;
		}

		&__text {
			margin: 0;
			color: #fff;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;

			:deep(.disclaimer-button) {
				margin-top: -4px;
				vertical-align: middle;
			}
		}

		&__free-trial {
			margin: 0;
			color: #ffe894;
			font-size: 14px;
			font-weight: 900;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin-bottom: 4px;

			:deep(.disclaimer-button) {
				margin-top: -4px;
				vertical-align: middle;
			}
		}

		&__price {
			margin: 0;
			color: #c6c8cd;
			font-size: 14px;
			font-weight: 400;
			line-height: 24px;
			letter-spacing: 0.21px;

			// &--breakdown {
			// 	@media (max-width: 1024px) {
			// 		margin-bottom: -24px;
			// 	}
			// }
		}
	}
}
.picture-wrapper {
	&:has(.ribbon) {
		position: relative;

		.picture-element {
			position: relative;
			z-index: 2;
		}
	}

	min-width: 120px;
	min-height: 40px;
	margin-bottom: auto;
	margin-inline-end: 48px;

	@media (max-width: 1345px) {
		min-width: 80px;
	}

	@media (max-width: 1024px) {
		margin-inline-end: 16px;
		margin-bottom: 10px;
	}
}

.leaving-banner-container {
	padding-top: 8px;
	background-color: inherit;
}

.leaving-banner {
	display: flex;
	padding: 8px 16px;
	background: rgba(250, 0, 42, 0.08);
	color: #fa002a;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.21px;
	align-items: center;
	gap: 8px;
}

.transparent {
	color: transparent;
}

.price-fast {
	font-size: 14px;
	line-height: 18px;
	padding-top: 6px;
}

.ribbon {
	position: absolute;
	width: 100%;
	max-width: 80px;
	text-align: center;
	background-color: $jw-color-lightgray;
	color: $jw-color-darkgray;
	font-size: 9px;
	font-weight: 700;
	padding-inline: 4px;
	padding-top: 8px;
	border-radius: 0 0 0 5px;
	z-index: 0;
	left: 0;
	bottom: -5px;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
}

// this element is added to deter ad blcockers from blocking the free trial
.offer__detail {
	display: none;
}

.offer-container {
	position: relative;
	display: block;
}
</style>
